/**
 * Sysadmin Catalog → Functions (PR-G).
 *
 * The Functions catalog leaf — sysadmin authoring UI for system
 * wrapper-functions, with per-leaf publish lifecycle.
 *
 * Differences from /sysadmin/models/functions (the M6c authoring
 * surface that this catalog leaf replaces in the IA):
 *   - Adds publish/unpublish lifecycle actions on top of the existing
 *     create/sign/activate flow (PR-B). Lifecycle states are
 *     draft → published → deprecated → retired.
 *   - Status pill column in the list view + chip-filter bar.
 *   - Lifecycle action bar in the detail view: Publish / Unpublish.
 *   - Tenants only see status='published' rows via /v1/functions/system.
 *
 * Mirrors the surface conventions from:
 *   - sysadmin/catalog.prompts.tsx (sibling catalog leaf, real on master)
 *   - sysadmin/models.functions.tsx (M6c PR-B authoring surface)
 *
 * Per I-M6.7 + I-M6.4 this surface is sysadmin-only. The /sysadmin
 * parent layout enforces requireSysadmin.
 *
 * See:
 *   - docs/SYSADMIN-PLATFORM-SPEC.md §3 Catalog
 *   - docs/waves/wave-3/M6-ANCHOR.md (invariants I-M6.x)
 *   - packages/api/src/routes/v1/admin/argus-models.ts
 *     (publish/unpublish + signing + CRUD endpoints)
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  Code2,
  Search,
  Plus,
  Eye,
  Power,
  PowerOff,
  AlertTriangle,
  CheckCircle2,
  Hash,
  ShieldCheck,
  PenSquare,
  ScanText,
  Layers,
  Cpu,
  Save,
  Signature,
  FileWarning,
  X,
  Trash2,
  Send,
  Archive,
  Loader2,
  Pencil,
  Calendar,
  Sparkles,
  FileText,
  ChevronDown,
} from 'lucide-react';
// Wave C PR-F (2026-05-13): replaced two inline <Editor> blocks (Source
// tab + NewFunctionSheet) with the shared <FunctionSourceEditor /> primitive.
// Variants 'sysadmin-source-tab' (420px) and 'sysadmin-create-sheet' (300px)
// preserve the exact pre-refactor 6-option presets + hard-coded vs-dark theme.
import { FunctionSourceEditor } from '@/components/function-editor-shared';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useWrapperFunctions,
  useWrapperFunctionUsage,
  useUpdateWrapperFunction,
  useCreateWrapperFunction,
  useUpdateWrapperFunctionSource,
  useSignWrapperFunction,
  useDeleteWrapperFunction,
  usePublishWrapperFunction,
  useUnpublishWrapperFunction,
  type WrapperFunction,
  type WrapperFunctionStatus,
} from '@/lib/use-argus-models';
import { cn } from '@/lib/utils';

// R5 P0-2 fix (2026-05-13): list now lives under index route ID
// `/sysadmin/catalog/functions/`; parent `catalog.functions.tsx` is a
// pure layout with <Outlet/> so the create child can render.
export const Route = createFileRoute('/sysadmin/catalog/functions/')({
  component: SysadminCatalogFunctionsPage,
});

// ─── Filter option lists ───────────────────────────────────────────────────

const STATUS_OPTIONS: Array<{ value: WrapperFunctionStatus | 'all'; label: string }> = [
  { value: 'all',         label: 'All' },
  { value: 'draft',       label: 'Draft' },
  { value: 'published',   label: 'Published' },
  { value: 'deprecated',  label: 'Deprecated' },
  { value: 'retired',     label: 'Retired' },
];

const SIGNED_OPTIONS = ['all', 'signed', 'unsigned'] as const;
const RUNTIME_OPTIONS = ['all', 'native', 'ts', 'py'] as const;
const APPLIES_TO_OPTIONS = ['all', 'inlet', 'outlet', 'both'] as const;

const CATEGORY_OPTIONS = [
  'classification', 'pii', 'prompt-injection', 'safety', 'memory',
  'retrieval', 'citation', 'faithfulness', 'guardrail', 'audit',
  'format', 'custom',
] as const;

const TS_TEMPLATE = `// TypeScript wrapper function — runs in the W3-CELL-07 sandbox.
// Receives the request context; returns a possibly-mutated context.
//
// Latency budget enforced at the runtime; keep this fast.

export async function run(ctx) {
  // TODO: implement
  return ctx;
}
`;

const PY_TEMPLATE = `# Python wrapper function — runs in the W3-CELL-07 sandbox.
# Receives the request context; returns a possibly-mutated context.
#
# Latency budget enforced at the runtime; keep this fast.

def run(ctx):
    # TODO: implement
    return ctx
`;

// ─── Page component ────────────────────────────────────────────────────────

function SysadminCatalogFunctionsPage() {
  const navigate = useNavigate();

  // Server-side filters
  const [statusFilter, setStatusFilter] =
    useState<(typeof STATUS_OPTIONS)[number]['value']>('all');

  // Client-side filters
  const [search, setSearch] = useState('');
  const [signedFilter, setSignedFilter] =
    useState<(typeof SIGNED_OPTIONS)[number]>('all');
  const [runtimeFilter, setRuntimeFilter] =
    useState<(typeof RUNTIME_OPTIONS)[number]>('all');
  const [appliesFilter, setAppliesFilter] =
    useState<(typeof APPLIES_TO_OPTIONS)[number]>('all');

  // Selection / dialogs
  const [selected, setSelected] = useState<WrapperFunction | null>(null);
  // functions-expansion (2026-05-12): the classic Sheet form is now a
  // secondary "Classic form" entry — the primary "+ New Function" CTA
  // routes to /sysadmin/catalog/functions/new (chat-driven authoring).
  // Preserved for parity with the previous static-form workflow.
  const [createOpen, setCreateOpen] = useState(false);

  const fnsQuery = useWrapperFunctions(
    statusFilter === 'all' ? {} : { status: statusFilter },
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (fnsQuery.data?.items ?? []).filter((f) => {
      if (runtimeFilter !== 'all' && f.runtime !== runtimeFilter) return false;
      if (appliesFilter !== 'all' && f.appliesTo !== appliesFilter) return false;
      if (signedFilter !== 'all') {
        const isSigned = f.runtime === 'native' || !!f.signingKeyId;
        if (signedFilter === 'signed' && !isSigned) return false;
        if (signedFilter === 'unsigned' && isSigned) return false;
      }
      if (!q) return true;
      return (
        f.functionKey.toLowerCase().includes(q) ||
        f.displayName.toLowerCase().includes(q) ||
        (f.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [fnsQuery.data, search, runtimeFilter, appliesFilter, signedFilter]);

  // Aggregate counts for the header summary.
  const counts = useMemo(() => {
    const c: Record<WrapperFunctionStatus | 'total', number> = {
      total: 0, draft: 0, published: 0, deprecated: 0, retired: 0,
    };
    for (const f of fnsQuery.data?.items ?? []) {
      c.total += 1;
      c[f.status] = (c[f.status] ?? 0) + 1;
    }
    return c;
  }, [fnsQuery.data]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Code2 className="h-6 w-6" />
            System Functions
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs font-mono">§3 Catalog</Badge>
            <Badge variant="secondary" className="text-xs">v1</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
            Sysadmin-authored wrapper functions that compose into Argus model
            chains. Tenants only see <strong>published</strong> functions via
            <code className="bg-muted px-1 rounded mx-1 text-xs">/v1/functions/system</code>.
            New rows ship as drafts; sign + activate + publish to make them tenant-visible.
          </p>
        </div>
        {/* Split button: primary action = chat-driven authoring;
            secondary = classic form. The chat-driven flow uses the
            sysadmin-scoped function_helper to draft name + description
            + source via Accept/Reject mutations on a Monaco canvas, then
            collects the sysadmin lifecycle metadata (function_key,
            category, applies_to, latency, version) in a footer strip.
            Mirrors how Tools + Skills evolved this week.

            See: /sysadmin/catalog/functions/new (new route)
                 catalog.functions.new.tsx (chat-driven page)
                 NewFunctionSheet (classic form preserved below) */}
        <div className="flex items-center gap-0 shrink-0">
          <Button
            size="sm"
            className="gap-1.5 rounded-r-none border-r border-r-primary-foreground/20"
            onClick={() => navigate({ to: '/sysadmin/catalog/functions/new' })}
            title="Author a new wrapper function via chat (AI-assisted)"
          >
            <Sparkles className="h-3.5 w-3.5" /> New Function
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                className="rounded-l-none px-2"
                title="More authoring options"
                aria-label="More create options"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onClick={() => navigate({ to: '/sysadmin/catalog/functions/new' })}
                className="cursor-pointer"
              >
                <Sparkles className="h-3.5 w-3.5 mr-2 text-primary" />
                <div className="flex flex-col">
                  <span className="text-xs font-medium">Chat-driven (default)</span>
                  <span className="text-[10px] text-muted-foreground">
                    AI helper drafts the function
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setCreateOpen(true)}
                className="cursor-pointer"
              >
                <FileText className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                <div className="flex flex-col">
                  <span className="text-xs font-medium">Classic form</span>
                  <span className="text-[10px] text-muted-foreground">
                    Static sheet (pre-2026-05-12 flow)
                  </span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Counts row */}
      <div className="grid grid-cols-5 gap-2">
        <CountTile label="Total" value={counts.total} icon={Layers} />
        <CountTile label="Draft" value={counts.draft} icon={Pencil} tone="muted" />
        <CountTile label="Published" value={counts.published} icon={CheckCircle2} tone="emerald" />
        <CountTile label="Deprecated" value={counts.deprecated} icon={AlertTriangle} tone="amber" />
        <CountTile label="Retired" value={counts.retired} icon={Archive} tone="red" />
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="py-3 space-y-3">
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="relative flex-1 w-full">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, slug, or description…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9 text-sm"
              />
            </div>
            <FilterPills
              label="Status"
              options={STATUS_OPTIONS.map((s) => s.value)}
              labels={Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.label]))}
              active={statusFilter}
              onPick={(v) => setStatusFilter(v)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <FilterPills
              label="Runtime"
              options={RUNTIME_OPTIONS}
              active={runtimeFilter}
              onPick={(v) => setRuntimeFilter(v)}
            />
            <FilterPills
              label="Phase"
              options={APPLIES_TO_OPTIONS}
              active={appliesFilter}
              onPick={(v) => setAppliesFilter(v)}
            />
            <FilterPills
              label="Signed"
              options={SIGNED_OPTIONS}
              active={signedFilter}
              onPick={(v) => setSignedFilter(v)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Body */}
      {fnsQuery.isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground animate-pulse text-sm">
            Loading functions…
          </CardContent>
        </Card>
      ) : fnsQuery.error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load: {fnsQuery.error.message}</div>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-2">
            <Code2 className="h-10 w-10 text-muted-foreground mx-auto" />
            <h3 className="text-base font-semibold">
              {fnsQuery.data?.items.length === 0
                ? 'No functions yet'
                : 'No functions match the current filters'}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {fnsQuery.data?.items.length === 0
                ? 'Author your first wrapper function with the button above.'
                : 'Adjust the filters above to widen the search.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-32">Status</TableHead>
                  <TableHead className="w-24">Signed</TableHead>
                  <TableHead className="w-24">Active</TableHead>
                  <TableHead className="w-28">Runtime</TableHead>
                  <TableHead className="w-20">Version</TableHead>
                  <TableHead className="w-44">Updated</TableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((fn) => {
                  const isNative = fn.runtime === 'native';
                  const isSigned = isNative || !!fn.signingKeyId;
                  return (
                    <TableRow
                      key={fn.id}
                      // #883 fix (fix-wave-3 PR-P1B-rest, 2026-05-17):
                      // Expose the API row UUID on the rendered TableRow so
                      // SYS-CATALOG-011 (and the matrix §5 "no mock data"
                      // assertion) can prove the list view is mounting live
                      // API rows. Without this attribute the React `key`
                      // never lands in the DOM and the test sees a
                      // populated table with no UUID strings to match.
                      data-row-id={fn.id}
                      data-row-slug={fn.functionKey}
                      className="cursor-pointer"
                      onClick={() => setSelected(fn)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <CategoryIcon category={fn.category} className="h-3.5 w-3.5 text-muted-foreground" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">
                              {fn.displayName}
                            </div>
                            <div className="text-[11px] text-muted-foreground font-mono truncate max-w-md">
                              {fn.functionKey}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusPill status={fn.status} />
                      </TableCell>
                      <TableCell>
                        {isSigned ? (
                          <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-[10px]">
                            <Signature className="h-2.5 w-2.5 mr-0.5" />
                            signed
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground text-[10px]">
                            <FileWarning className="h-2.5 w-2.5 mr-0.5" />
                            unsigned
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {fn.isActive ? (
                          <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-[10px]">
                            active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground text-[10px]">
                            inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <RuntimeBadge runtime={fn.runtime} />
                      </TableCell>
                      <TableCell className="text-xs font-mono tabular-nums text-muted-foreground">
                        v{fn.version}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground tabular-nums">
                        {new Date(fn.updatedAt).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          title="View / edit"
                          onClick={() => setSelected(fn)}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        Showing {filtered.length} of {fnsQuery.data?.items.length ?? 0} functions
        {statusFilter !== 'all' && ` (filtered to ${statusFilter})`}.
      </p>

      <FunctionDetailDialog
        fn={selected}
        onClose={() => setSelected(null)}
        onChanged={(next) => setSelected(next)}
      />

      <NewFunctionSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}

// ─── Detail dialog ─────────────────────────────────────────────────────────

function FunctionDetailDialog({
  fn,
  onClose,
  onChanged,
}: {
  fn: WrapperFunction | null;
  onClose: () => void;
  onChanged: (next: WrapperFunction) => void;
}) {
  const usageQuery = useWrapperFunctionUsage(fn?.id ?? null);
  const updateMutation = useUpdateWrapperFunction();
  const updateSource = useUpdateWrapperFunctionSource();
  const signFn = useSignWrapperFunction();
  const deleteFn = useDeleteWrapperFunction();
  const publishFn = usePublishWrapperFunction();
  const unpublishFn = useUnpublishWrapperFunction();

  const [activeTab, setActiveTab] = useState<'overview' | 'source' | 'versions' | 'assignments' | 'audit'>('overview');
  const [draftSource, setDraftSource] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [unpublishMode, setUnpublishMode] = useState<'deprecated' | 'retired'>('deprecated');
  const [unpublishReason, setUnpublishReason] = useState('');

  // Reset draftSource whenever the displayed function changes.
  useMemo(() => {
    setDraftSource(fn?.source ?? '');
    return null;
  }, [fn?.id, fn?.source]);

  if (!fn) return null;

  const isNative = fn.runtime === 'native';
  const sourceDirty = !isNative && draftSource !== fn.source;
  const isSigned = isNative || !!fn.signingKeyId;

  const canPublish = fn.status !== 'published'
    && fn.status !== 'retired'
    && fn.isActive
    && (isNative || !!fn.signature);

  const handleSaveSource = async () => {
    if (!fn || !sourceDirty) return;
    try {
      const result = await updateSource.mutateAsync({ id: fn.id, source: draftSource });
      toast.success('Source saved (signature invalidated — re-sign before re-activating)');
      onChanged({
        ...fn,
        source: draftSource,
        isActive: result.isActive,
        signature: null,
        signingKeyId: null,
      } as WrapperFunction);
    } catch (err) {
      toast.error('Save failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const handleSign = async () => {
    if (!fn) return;
    try {
      const result = await signFn.mutateAsync({ id: fn.id });
      toast.success(`Signed with key ${result.signingKeyId}`);
      onChanged({ ...fn, signingKeyId: result.signingKeyId } as WrapperFunction);
    } catch (err) {
      toast.error('Signing failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const handleToggleActive = async (next: boolean) => {
    if (!fn) return;
    try {
      await updateMutation.mutateAsync({ id: fn.id, isActive: next });
      onChanged({ ...fn, isActive: next });
      toast.success(next ? 'Function activated' : 'Function deactivated');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      if (msg.includes('UNSIGNED_FUNCTION')) {
        toast.error('Cannot activate an unsigned function', {
          description: 'Save + sign the source first.',
        });
      } else if (msg.includes('NOT_PUBLISHED')) {
        toast.error('Cannot activate a draft', {
          description: 'Publish the function first.',
        });
      } else {
        toast.error('Toggle failed', { description: msg });
      }
    }
  };

  const handlePublish = async () => {
    if (!fn) return;
    try {
      const result = await publishFn.mutateAsync({ id: fn.id });
      toast.success(`Published "${fn.functionKey}"`, {
        description: 'Tenants now see this function via /v1/functions/system.',
      });
      onChanged({
        ...fn,
        status: 'published',
        publishedAt: result.publishedAt,
        publishedBy: result.publishedBy,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      if (msg.includes('NOT_ACTIVE')) {
        toast.error('Cannot publish an inactive function', {
          description: 'Sign + activate before publishing.',
        });
      } else if (msg.includes('UNSIGNED_FUNCTION')) {
        toast.error('Cannot publish unsigned function', {
          description: 'Sign the source first.',
        });
      } else if (msg.includes('CANNOT_PUBLISH_RETIRED')) {
        toast.error('Cannot publish a retired function', {
          description: 'Create a new version instead.',
        });
      } else {
        toast.error('Publish failed', { description: msg });
      }
    }
  };

  const openUnpublish = (mode: 'deprecated' | 'retired') => {
    setUnpublishMode(mode);
    setUnpublishReason('');
    setUnpublishOpen(true);
  };

  const handleUnpublish = async () => {
    if (!fn) return;
    if (unpublishReason.trim().length < 10) {
      toast.error('Reason required (at least 10 characters)');
      return;
    }
    try {
      const result = await unpublishFn.mutateAsync({
        id: fn.id,
        reason: unpublishReason.trim(),
        nextStatus: unpublishMode,
      });
      toast.success(
        unpublishMode === 'deprecated'
          ? `Deprecated "${fn.functionKey}"`
          : `Retired "${fn.functionKey}"`,
        {
          description:
            unpublishMode === 'deprecated'
              ? 'Existing tenant assignments keep working; no new tenants get visibility.'
              : 'Function taken offline. is_active forced false.',
        },
      );
      onChanged({
        ...fn,
        status: result.status,
        isActive: result.isActive,
        unpublishedAt: result.unpublishedAt,
        unpublishedBy: result.unpublishedBy,
        unpublishReason: result.unpublishReason,
      });
      setUnpublishOpen(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Unpublish failed', { description: msg });
    }
  };

  const handleDelete = async () => {
    if (!fn) return;
    try {
      await deleteFn.mutateAsync({ id: fn.id });
      toast.success('Function deleted');
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      if (msg.includes('FUNCTION_IN_USE')) {
        toast.error('Cannot delete', {
          description: 'Still wired into a brand chain. Remove from chains first.',
        });
      } else if (msg.includes('CANNOT_DELETE_NATIVE')) {
        toast.error('Cannot delete a native function', {
          description: 'Native rows are seeded; deactivate instead.',
        });
      } else {
        toast.error('Delete failed', { description: msg });
      }
      setConfirmDelete(false);
    }
  };

  return (
    <Dialog open={!!fn} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <CategoryIcon category={fn.category} className="h-5 w-5 text-muted-foreground" />
            {fn.displayName}
            <StatusPill status={fn.status} />
            {isNative && (
              <Badge variant="outline" className="text-blue-700 border-blue-300 text-[10px] ml-1">
                native
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">{fn.functionKey}</DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as typeof activeTab)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <TabsList className="grid grid-cols-5 w-full max-w-2xl">
            <TabsTrigger value="overview">
              <ScanText className="h-3.5 w-3.5 mr-1.5" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="source">
              <Code2 className="h-3.5 w-3.5 mr-1.5" />
              Source
            </TabsTrigger>
            <TabsTrigger value="versions">
              <Layers className="h-3.5 w-3.5 mr-1.5" />
              Versions
            </TabsTrigger>
            <TabsTrigger value="assignments">
              <Send className="h-3.5 w-3.5 mr-1.5" />
              Assignments
            </TabsTrigger>
            <TabsTrigger value="audit">
              <PenSquare className="h-3.5 w-3.5 mr-1.5" />
              Audit
            </TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="flex-1 overflow-auto px-1 py-3">
            <div className="space-y-4 text-sm">
              <Field label="Description" value={fn.description} />
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Runtime" value={<RuntimeBadge runtime={fn.runtime} />} />
                <Field label="Applies to" value={<AppliesToBadge appliesTo={fn.appliesTo} />} />
                <Field label="Category" value={fn.category} mono />
                <Field
                  label="Latency budget"
                  value={
                    <span className="font-mono">
                      {fn.latencyBudgetMs}
                      <span className="text-muted-foreground">ms</span>
                    </span>
                  }
                />
                <Field label="Version" value={<span className="font-mono">v{fn.version}</span>} />
                <Field
                  label="Active"
                  value={
                    fn.isActive ? (
                      <Badge variant="outline" className="text-emerald-700 border-emerald-300">active</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">inactive</Badge>
                    )
                  }
                />
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4 text-xs">
                <Field
                  label="Created"
                  value={
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      {fn.createdAt.split('T')[0]}
                    </span>
                  }
                />
                <Field
                  label="Updated"
                  value={
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      {fn.updatedAt.split('T')[0]}
                    </span>
                  }
                />
              </div>
              {(fn.publishedAt || fn.unpublishedAt) && (
                <>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    {fn.publishedAt && (
                      <Field
                        label="Published"
                        value={
                          <span className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                            <CheckCircle2 className="h-3 w-3" />
                            {fn.publishedAt.split('T')[0]}
                          </span>
                        }
                      />
                    )}
                    {fn.unpublishedAt && (
                      <Field
                        label={fn.status === 'retired' ? 'Retired' : 'Deprecated'}
                        value={
                          <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
                            {fn.status === 'retired' ? <Archive className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                            {fn.unpublishedAt.split('T')[0]}
                          </span>
                        }
                      />
                    )}
                  </div>
                </>
              )}
              {fn.unpublishReason && (
                <Card className="bg-amber-50/50 border-amber-200 dark:bg-amber-950/20">
                  <CardContent className="py-3 text-xs space-y-0.5">
                    <div className="font-medium text-amber-900 dark:text-amber-200">
                      {fn.status === 'retired' ? 'Retirement reason' : 'Deprecation reason'}
                    </div>
                    <div className="text-amber-800 dark:text-amber-300">{fn.unpublishReason}</div>
                  </CardContent>
                </Card>
              )}
              <Separator />
              <Field
                label="ID"
                value={<code className="font-mono text-[11px] text-muted-foreground">{fn.id}</code>}
              />
            </div>
          </TabsContent>

          {/* Source */}
          <TabsContent value="source" className="flex-1 overflow-hidden px-0 py-2 space-y-2">
            {isNative && (
              <Card className="bg-blue-50/50 border-blue-200 dark:bg-blue-950/20">
                <CardContent className="py-2 text-xs flex items-start gap-2">
                  <FileWarning className="h-3.5 w-3.5 shrink-0 mt-0.5 text-blue-700 dark:text-blue-400" />
                  <span className="text-blue-900 dark:text-blue-300">
                    Native function — source is a pointer to a compiled-in module, not editable bytes.
                    Returns 409 NATIVE_FUNCTION_IMMUTABLE on edit attempts.
                  </span>
                </CardContent>
              </Card>
            )}
            {/* Shared <FunctionSourceEditor variant="sysadmin-source-tab" />
                Preserves the exact 6-option preset + 420px height + vs-dark theme. */}
            <FunctionSourceEditor
              variant="sysadmin-source-tab"
              language={fn.runtime as 'ts' | 'py' | 'native'}
              value={isNative ? fn.source : draftSource}
              readOnly={isNative}
              onChange={(v) => { if (!isNative) setDraftSource(v); }}
            />
            {!isNative && (
              <div className="flex items-center justify-between gap-3 px-1">
                <div className="text-xs flex items-center gap-1.5">
                  {sourceDirty ? (
                    <span className="text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Unsaved edits — saving will invalidate the signature
                    </span>
                  ) : isSigned ? (
                    <span className="text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1">
                      <Signature className="h-3 w-3" />
                      Signed by {fn.signingKeyId}
                    </span>
                  ) : (
                    <span className="text-muted-foreground inline-flex items-center gap-1">
                      <FileWarning className="h-3 w-3" />
                      Unsigned — must sign before activation
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!sourceDirty || updateSource.isPending}
                    onClick={handleSaveSource}
                    className="gap-1.5"
                    title="Save source (invalidates signature)"
                  >
                    <Save className="h-3.5 w-3.5" />
                    {updateSource.isPending ? 'Saving…' : 'Save source'}
                  </Button>
                  <Button
                    size="sm"
                    disabled={sourceDirty || isSigned || signFn.isPending}
                    onClick={handleSign}
                    className="gap-1.5"
                    title={
                      sourceDirty
                        ? 'Save the source first'
                        : isSigned
                          ? 'Already signed'
                          : 'Sign current source with the platform Ed25519 key'
                    }
                  >
                    <Signature className="h-3.5 w-3.5" />
                    {signFn.isPending ? 'Signing…' : 'Sign'}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Versions */}
          <TabsContent value="versions" className="flex-1 overflow-auto px-1 py-3">
            <Card>
              <CardContent className="py-6 text-center space-y-2">
                <Layers className="h-8 w-8 text-muted-foreground mx-auto" />
                <p className="text-sm font-medium">Single-version surface</p>
                <p className="text-xs text-muted-foreground max-w-md mx-auto">
                  v1 of the Functions catalog tracks a single source body per
                  function row, with re-signing required after every edit.
                  Multi-version history is on the v2 roadmap (see SYSADMIN-PLATFORM-SPEC §3
                  Catalog).
                </p>
                <p className="text-xs text-muted-foreground">
                  Current version: <code className="font-mono">{fn.version}</code>
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assignments */}
          <TabsContent value="assignments" className="flex-1 overflow-auto px-1 py-3">
            <AssignmentsPanel
              fn={fn}
              loading={usageQuery.isLoading}
              error={usageQuery.error}
              items={usageQuery.data?.items ?? []}
              trustToggleCount={usageQuery.data?.trustToggleCount ?? 0}
            />
          </TabsContent>

          {/* Audit */}
          <TabsContent value="audit" className="flex-1 overflow-auto px-1 py-3">
            <Card>
              <CardContent className="py-6 text-center space-y-2">
                <PenSquare className="h-8 w-8 text-muted-foreground mx-auto" />
                <p className="text-sm font-medium">Audit trail in /sysadmin/audit</p>
                <p className="text-xs text-muted-foreground max-w-md mx-auto">
                  Every action on this function (create, source-edit, sign, activate,
                  publish, unpublish, delete) is appended to the platform audit chain.
                  Filter on <code className="bg-muted px-1 rounded">resourceType=argus_wrapper_function</code>
                  {' '}+ <code className="bg-muted px-1 rounded">resourceId={fn.id.slice(0, 8)}…</code>
                  {' '}in the Audit module.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t pt-4 gap-2 flex-wrap">
          {!isNative && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:bg-destructive/10 mr-auto"
              onClick={() => setConfirmDelete(true)}
              title="Permanently delete this function"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>

          {/* Lifecycle action bar — driven by current status */}
          {fn.status === 'draft' && (
            <>
              {fn.isActive ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggleActive(false)}
                  disabled={updateMutation.isPending}
                  title="Deactivate"
                >
                  <PowerOff className="h-4 w-4 mr-1" />
                  Deactivate
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggleActive(true)}
                  disabled={!isSigned || updateMutation.isPending}
                  title={isSigned ? 'Activate (still draft until published)' : 'Sign first'}
                >
                  <Power className="h-4 w-4 mr-1" />
                  Activate
                </Button>
              )}
              <Button
                onClick={handlePublish}
                disabled={!canPublish || publishFn.isPending}
                title={
                  canPublish
                    ? 'Publish to tenants — appears in /v1/functions/system'
                    : !fn.isActive
                      ? 'Activate the function first'
                      : !isSigned
                        ? 'Sign the source first'
                        : 'Cannot publish'
                }
              >
                {publishFn.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                Publish
              </Button>
            </>
          )}

          {/*
            Issue #746 — PR-U1 (CATALOG-MIGRATION-DESIGN §6.11) collapsed
            wrapper-function lifecycle from a 4-state DAG
            (draft -> published -> deprecated -> retired) to a 2-state
            Q7 model (draft|published) with `enabled` for soft-disable
            and `deleted_at` for soft-delete. The legacy Deprecate /
            Retire / Re-publish buttons all routed to
            POST /argus-wrapper-functions/:id/unpublish, which now
            returns 410 Gone with copy directing operators to the
            replacement actions. To stop exposing dead controls, the
            three unreachable-or-broken branches are removed. For a
            `published` row, soft-disable lives on the parent details
            tab (Enabled toggle) and soft-delete is the existing Delete
            button below. `deprecated` and `retired` are no longer
            persisted by the backend so those branches are also
            unreachable.
          */}
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete wrapper function</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete <code className="bg-muted px-1 rounded">{fn.functionKey}</code>?
              This refuses if the function is still wired into any brand chain —
              remove from chains first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete function
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UnpublishReasonDialog
        open={unpublishOpen}
        mode={unpublishMode}
        functionKey={fn.functionKey}
        reason={unpublishReason}
        onReasonChange={setUnpublishReason}
        onCancel={() => setUnpublishOpen(false)}
        onConfirm={handleUnpublish}
        pending={unpublishFn.isPending}
      />
    </Dialog>
  );
}

// ─── Unpublish reason modal ────────────────────────────────────────────────

function UnpublishReasonDialog({
  open,
  mode,
  functionKey,
  reason,
  onReasonChange,
  onCancel,
  onConfirm,
  pending,
}: {
  open: boolean;
  mode: 'deprecated' | 'retired';
  functionKey: string;
  reason: string;
  onReasonChange: (r: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  pending: boolean;
}) {
  const reasonValid = reason.trim().length >= 10;
  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {mode === 'retired' ? (
              <Archive className="h-5 w-5 text-destructive" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-700" />
            )}
            {mode === 'retired' ? 'Retire' : 'Deprecate'} wrapper function?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                {mode === 'retired'
                  ? 'Retiring takes the function offline and forces is_active=false. The runtime resolver will skip it. Existing tenant chain references will silently no-op until removed.'
                  : 'Deprecating keeps existing tenant assignments working but stops new tenants from seeing it via /v1/functions/system. is_active stays where it is.'}
              </p>
              <p>
                <code className="bg-muted px-1 rounded">{functionKey}</code>
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="unpublish-reason" className="text-xs">
                  Reason (required, ≥ 10 chars)
                </Label>
                <Textarea
                  id="unpublish-reason"
                  value={reason}
                  onChange={(e) => onReasonChange(e.target.value)}
                  rows={3}
                  placeholder={
                    mode === 'retired'
                      ? 'Why is this function being retired? (audit trail)'
                      : 'Why is this function being deprecated? (audit trail)'
                  }
                  className="text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  Surfaces in the audit log + sysadmin detail UI.
                  {!reasonValid && reason.length > 0 && (
                    <span className="text-amber-700 ml-1">
                      ({reason.trim().length}/10 chars)
                    </span>
                  )}
                </p>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!reasonValid || pending}
            onClick={onConfirm}
            className={cn(
              mode === 'retired'
                ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground'
                : 'bg-amber-600 hover:bg-amber-700 text-white',
            )}
          >
            {pending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {mode === 'retired' ? 'Retire' : 'Deprecate'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Assignments panel ─────────────────────────────────────────────────────

function AssignmentsPanel({
  fn,
  loading,
  error,
  items,
  trustToggleCount,
}: {
  fn: WrapperFunction;
  loading: boolean;
  error: Error | null;
  items: Array<{
    argusModelId: string;
    brandId: string;
    displayName: string;
    modality: string;
    chainKind: 'inlet' | 'outlet';
    position: number;
    isMandatory: boolean;
    failurePolicy: 'terminal' | 'warn';
    isActive: boolean;
  }>;
  trustToggleCount: number;
}) {
  if (loading) {
    return <div className="text-sm text-muted-foreground animate-pulse">Loading usage…</div>;
  }
  if (error) {
    return <div className="text-sm text-destructive">Failed to load usage: {error.message}</div>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Tenant visibility</CardTitle>
          <CardDescription className="text-xs">
            How tenants currently see this function.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {fn.status === 'published' ? (
            <div className="flex items-start gap-2 text-emerald-700 dark:text-emerald-400">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Auto-assigned to all tenants on publish — surfaces in
                <code className="bg-muted px-1 rounded mx-1 text-xs">/v1/functions/system</code>.
              </span>
            </div>
          ) : fn.status === 'deprecated' ? (
            <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Deprecated — existing tenant chain assignments keep executing.
                New tenants do NOT see this function in
                <code className="bg-muted px-1 rounded mx-1 text-xs">/v1/functions/system</code>.
              </span>
            </div>
          ) : fn.status === 'retired' ? (
            <div className="flex items-start gap-2 text-destructive">
              <Archive className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Retired — function is offline. Tenants do NOT see it; runtime resolver skips it.
              </span>
            </div>
          ) : (
            <div className="flex items-start gap-2 text-muted-foreground">
              <Pencil className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Draft — never seen by tenants. Publish to make it tenant-visible.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {(items.length > 0 || trustToggleCount > 0) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Wired into</CardTitle>
            <CardDescription className="text-xs">
              Argus brands + Trust toggles that reference this function.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {trustToggleCount > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="outline" className="text-amber-700 border-amber-300">
                  {trustToggleCount} Trust {trustToggleCount === 1 ? 'toggle' : 'toggles'}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Manage on the Trust Toggles page.
                </span>
              </div>
            )}
            {items.length > 0 && (
              <div className="space-y-1">
                {items.map((u, idx) => (
                  <div
                    key={`${u.argusModelId}-${u.chainKind}-${u.position}-${idx}`}
                    className={cn(
                      'flex items-center gap-3 border rounded-md p-2 text-sm',
                      !u.isActive && 'opacity-60',
                    )}
                  >
                    <span className="font-mono text-xs text-muted-foreground tabular-nums w-12 shrink-0">
                      #{u.position}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{u.displayName}</div>
                      <div className="text-[11px] text-muted-foreground font-mono mt-0.5 truncate">
                        {u.brandId}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Badge variant="outline" className="text-[10px]">
                        {u.chainKind}
                      </Badge>
                      {u.isMandatory ? (
                        <Badge variant="default" className="text-[10px]">mandatory</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-[10px]">toggleable</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── New function sheet ────────────────────────────────────────────────────

function NewFunctionSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreateWrapperFunction();
  const [functionKey, setFunctionKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [runtime, setRuntime] = useState<'ts' | 'py'>('ts');
  const [source, setSource] = useState(TS_TEMPLATE);
  const [category, setCategory] = useState<string>('custom');
  const [appliesTo, setAppliesTo] = useState<'inlet' | 'outlet' | 'both'>('inlet');
  const [latencyBudgetMs, setLatencyBudgetMs] = useState<number>(200);
  const [version, setVersion] = useState<string>('1.0.0');

  const handleRuntimeChange = (next: 'ts' | 'py') => {
    setRuntime(next);
    if (source === TS_TEMPLATE || source === PY_TEMPLATE) {
      setSource(next === 'ts' ? TS_TEMPLATE : PY_TEMPLATE);
    }
  };

  const reset = () => {
    setFunctionKey('');
    setDisplayName('');
    setDescription('');
    setRuntime('ts');
    setSource(TS_TEMPLATE);
    setCategory('custom');
    setAppliesTo('inlet');
    setLatencyBudgetMs(200);
    setVersion('1.0.0');
  };

  const canSubmit =
    functionKey.trim().length > 0 &&
    /^[a-z0-9_.-]+$/.test(functionKey.trim()) &&
    displayName.trim().length > 0 &&
    description.trim().length > 0 &&
    source.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await create.mutateAsync({
        functionKey: functionKey.trim(),
        displayName: displayName.trim(),
        description: description.trim(),
        runtime,
        source,
        category,
        appliesTo,
        latencyBudgetMs,
        version,
      });
      toast.success(`Created "${functionKey.trim()}" as a draft`, {
        description: 'Open it from the table to sign + activate + publish.',
      });
      reset();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      if (msg.includes('FUNCTION_KEY_TAKEN')) {
        toast.error('Function key taken', {
          description: 'Pick a different key — keys are unique.',
        });
      } else {
        toast.error('Create failed', { description: msg });
      }
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <SheetContent side="right" className="sm:max-w-3xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New wrapper function</SheetTitle>
          <SheetDescription>
            New rows ship as drafts (status=draft, is_active=false, signature=null).
            From the detail dialog: save source → sign → activate → publish.
            Tenants only see published rows.
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cf-key" className="text-xs">Function key (slug)</Label>
              <Input
                id="cf-key"
                value={functionKey}
                onChange={(e) => setFunctionKey(e.target.value)}
                placeholder="argus.my_filter"
                className="font-mono text-xs h-8"
              />
              <p className="text-[10px] text-muted-foreground">
                Lowercase alphanumeric with . _ - only.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cf-runtime" className="text-xs">Runtime</Label>
              <Select value={runtime} onValueChange={(v) => handleRuntimeChange(v as 'ts' | 'py')}>
                <SelectTrigger id="cf-runtime" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ts" className="text-xs">TypeScript</SelectItem>
                  <SelectItem value="py" className="text-xs">Python</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cf-display" className="text-xs">Display name</Label>
            <Input
              id="cf-display"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="My Filter"
              className="text-sm h-8"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cf-description" className="text-xs">Description</Label>
            <Textarea
              id="cf-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this filter does and when it runs."
              rows={2}
              className="text-sm"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cf-category" className="text-xs">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="cf-category" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cf-applies" className="text-xs">Applies to</Label>
              <Select value={appliesTo} onValueChange={(v) => setAppliesTo(v as 'inlet' | 'outlet' | 'both')}>
                <SelectTrigger id="cf-applies" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inlet" className="text-xs">inlet</SelectItem>
                  <SelectItem value="outlet" className="text-xs">outlet</SelectItem>
                  <SelectItem value="both" className="text-xs">both</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cf-latency" className="text-xs">Latency (ms)</Label>
              <Input
                id="cf-latency"
                type="number"
                min={1}
                max={10000}
                value={latencyBudgetMs}
                onChange={(e) => setLatencyBudgetMs(Number(e.target.value) || 0)}
                className="text-xs h-8"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cf-version" className="text-xs">Version</Label>
            <Input
              id="cf-version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="1.0.0"
              className="font-mono text-xs h-8 max-w-32"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Source</Label>
            {/* Shared <FunctionSourceEditor variant="sysadmin-create-sheet" />
                Preserves the exact 6-option preset + 300px height + vs-dark theme. */}
            <FunctionSourceEditor
              variant="sysadmin-create-sheet"
              language={runtime}
              value={source}
              onChange={(v) => setSource(v)}
            />
            <p className="text-[11px] text-muted-foreground">
              Drafts ship with is_active=false and no signature. From the
              detail dialog: save → sign → activate → publish.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => { reset(); onClose(); }}>
              <X className="h-3.5 w-3.5 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!canSubmit || create.isPending}
              onClick={handleSubmit}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              {create.isPending ? 'Creating…' : 'Create draft'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function FilterPills<T extends string>({
  label,
  options,
  active,
  onPick,
  labels,
}: {
  label: string;
  options: readonly T[];
  active: T;
  onPick: (v: T) => void;
  labels?: Record<string, string>;
}) {
  return (
    <div className="flex items-center gap-1 shrink-0 flex-wrap">
      <span className="text-xs text-muted-foreground mr-1">{label}:</span>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onPick(opt)}
          className={cn(
            'rounded-full px-2.5 py-1 text-[11px] font-medium border transition-colors',
            active === opt
              ? 'bg-primary text-primary-foreground border-primary'
              : 'border-input text-muted-foreground hover:bg-muted/40',
          )}
        >
          {labels?.[opt] ?? opt}
        </button>
      ))}
    </div>
  );
}

function StatusPill({ status }: { status: WrapperFunctionStatus }) {
  const map: Record<
    WrapperFunctionStatus,
    { label: string; className: string; Icon: typeof CheckCircle2 }
  > = {
    draft: {
      label: 'draft',
      className: 'border-muted-foreground/40 text-muted-foreground',
      Icon: Pencil,
    },
    published: {
      label: 'published',
      className: 'border-emerald-300 text-emerald-700 dark:text-emerald-400',
      Icon: CheckCircle2,
    },
    deprecated: {
      label: 'deprecated',
      className: 'border-amber-300 text-amber-700 dark:text-amber-400',
      Icon: AlertTriangle,
    },
    retired: {
      label: 'retired',
      className: 'border-red-300 text-red-700 dark:text-red-400',
      Icon: Archive,
    },
  };
  const { label, className, Icon } = map[status];
  return (
    <Badge variant="outline" className={cn('text-[10px] gap-1', className)}>
      <Icon className="h-2.5 w-2.5" />
      {label}
    </Badge>
  );
}

function CountTile({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: number;
  icon: typeof Layers;
  tone?: 'muted' | 'emerald' | 'amber' | 'red';
}) {
  const toneClass =
    tone === 'emerald' ? 'text-emerald-700 dark:text-emerald-400'
    : tone === 'amber' ? 'text-amber-700 dark:text-amber-400'
    : tone === 'red' ? 'text-red-700 dark:text-red-400'
    : tone === 'muted' ? 'text-muted-foreground'
    : 'text-foreground';
  return (
    <Card>
      <CardContent className="py-3 flex items-center gap-3">
        <Icon className={cn('h-4 w-4 shrink-0', toneClass)} />
        <div className="min-w-0">
          <div className={cn('text-lg font-semibold tabular-nums leading-tight', toneClass)}>
            {value}
          </div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
            {label}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RuntimeBadge({ runtime }: { runtime: string }) {
  const tone =
    runtime === 'native' ? 'border-blue-300 text-blue-700'
    : runtime === 'ts' ? 'border-emerald-300 text-emerald-700'
    : runtime === 'py' ? 'border-amber-300 text-amber-700'
    : 'border-muted text-muted-foreground';
  return (
    <Badge variant="outline" className={cn('text-[10px] gap-1', tone)}>
      <Cpu className="h-2.5 w-2.5" />
      {runtime}
    </Badge>
  );
}

function AppliesToBadge({ appliesTo }: { appliesTo: string }) {
  const tone =
    appliesTo === 'inlet' ? 'border-blue-300 text-blue-700'
    : appliesTo === 'outlet' ? 'border-purple-300 text-purple-700'
    : appliesTo === 'both' ? 'border-violet-300 text-violet-700'
    : 'border-muted text-muted-foreground';
  return (
    <Badge variant="outline" className={cn('text-[10px]', tone)}>
      {appliesTo}
    </Badge>
  );
}

function CategoryIcon({ category, className }: { category: string; className?: string }) {
  switch (category) {
    case 'classification':
      return <Hash className={className} />;
    case 'pii':
    case 'prompt-injection':
    case 'safety':
      return <ShieldCheck className={className} />;
    case 'audit':
      return <PenSquare className={className} />;
    case 'faithfulness':
    case 'citation':
    case 'retrieval':
      return <ScanText className={className} />;
    case 'memory':
      return <Layers className={className} />;
    default:
      return <Code2 className={className} />;
  }
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
        {label}
      </div>
      <div className={cn('text-sm', mono && 'font-mono')}>{value}</div>
    </div>
  );
}

// runtimeToMonacoLang() removed in Wave C PR-F (2026-05-13). The shared
// <FunctionSourceEditor> primitive accepts 'ts' | 'py' | 'native' directly
// via its language prop — see components/function-editor-shared/
// FunctionSourceEditor.helpers.ts functionLanguageToMonacoLang() for the
// canonical mapping.
